import IconBin from "@shared/components/icons/Bin"
import IconBookLibrary from "@shared/components/icons/BookLibrary"
import IconCog1 from "@shared/components/icons/Cog1"
import IconCogLine from "@shared/components/icons/CogLine"
import IconCommonFileDouble from "@shared/components/icons/CommonFileDouble"
import IconCopy from "@shared/components/icons/Copy"
import IconCopyPaste from "@shared/components/icons/CopyPaste"
import IconDoubleArrowRight from "@shared/components/icons/DoubleArrowRight"
import IconDownload from "@shared/components/icons/Download"
import IconEmail from "@shared/components/icons/Email"
import IconFileRotate from "@shared/components/icons/FileRotate"
import IconFlashColor from "@shared/components/icons/FlashColor"
import IconFullscreen from "@shared/components/icons/Fullscreen"
import IconGoogleLogo from "@shared/components/icons/GoogleLogo"
import IconHistoryClock from "@shared/components/icons/HistoryClock"
import IconHyperlink from "@shared/components/icons/Hyperlink"
import IconMinusCircle from "@shared/components/icons/MinusCircle"
import IconNavArrowLeft from "@shared/components/icons/NavArrowLeft"
import IconNavArrowRight from "@shared/components/icons/NavArrowRight"
import IconPrint from "@shared/components/icons/Print"
import IconPrintText from "@shared/components/icons/PrintText"
import IconRepo from "@shared/components/icons/Repo"
import IconSubtractCircle from "@shared/components/icons/SubtractCircle"
import IconSyncArrowClock from "@shared/components/icons/SyncArrowClock"
import {
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
  Section,
  SectionChevron,
  SectionHeading,
  SectionIcon,
  Sections,
  SectionTitle,
} from "@shared/components/menus/DrilldownMenu"
import { ConfirmView } from "@shared/components/menus/views/ConfirmView"
import { InfoView } from "@shared/components/menus/views/InfoView"
import { TemplateNameView } from "@shared/components/menus/views/TemplateNameView"
import type { UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import { tracked } from "@supergrain/kernel/react"
import { devLessonEditorSwitch } from "cc-frontend/lib/dev-lesson-editor-switch"
import { ReplaceWithTemplateView } from "../ReplaceWithTemplateView"
import CourseHeaderMenuTrigger from "./CourseHeaderMenuTrigger"
import { studentLessonUrl } from "./lesson-links"
import { expandedSections } from "./lesson-menu-sections"
import { DefaultTemplateNote } from "./lesson-menu-views/DefaultTemplateNote"
import type { RemoveClassTodayChoice } from "./lesson-menu-views/remove-class-today-flow"
import { removeClassTodayFlow } from "./lesson-menu-views/remove-class-today-flow"
import { ShareLinkView } from "./lesson-menu-views/ShareLinkView"

export interface Props {
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  course: Course
  planbook: Planbook
  courseDate: CourseDateCombined
  /** Opens the edit-course modal for the given courseId. */
  editCourseTitleAndOrder: (courseId: string) => void
  pasteLesson: () => void
  canPasteLesson: boolean
  /** Whether the lesson is already shown full screen; hides the Full Screen menu item. */
  isFullScreen?: boolean
  /** Copies the lesson to the user's clipboard (or prompts sign-in) and toasts. */
  copyLesson: () => void
  /** Opens the Import Lessons modal. */
  copyFromAnotherClass: () => void
  bumpLessonForward: () => void
  /** Deletes this day's lesson (if any) and pulls the future lessons back. */
  pullLessonBackwards: () => void
  /** This day holds a unit that starts and ends here, which blocks pulling backwards. */
  dayHasTinyUnit: boolean
  /** Deletes this day's lesson along with its version history. */
  eraseLesson: () => void
  /** Marks the class as not meeting today: lesson fate (bump/delete) plus an optional note. */
  removeClassToday: (choice: RemoveClassTodayChoice) => void
  /** Opens the Email Link dialog for the current lesson. */
  emailLesson: () => void
  printOrDownload: () => void
  /** Hands the class-website link to Google Classroom's share page. */
  shareToGoogleClassroom: () => void
  browseLessonHistory: () => void
  openFullScreen: () => void
  /** Opens the template-library modal (the Replace with Template drilldown's gallery row). */
  openTemplateGallery: () => void
  /** Pastes the given template over this lesson. */
  pasteTemplate: (templateId: string) => void
  saveAsTemplate: (name: string) => void
  /**
   * Makes the given template what this class starts its unplanned days from. Lessons already in
   * the plan are untouched — the view says so.
   */
  setDefaultTemplate: (templateId: string) => void
  /** Opens the template-library modal to pick the default from everything published. */
  openDefaultTemplateGallery: () => void
  /**
   * The template this class already starts its unplanned days from, checked in the chooser. Null
   * when its days were set individually and no one template is the class's answer.
   */
  defaultTemplateId: string | null
  /** Called when the menu opens or closes. */
  onOpenChange?: (isOpen: boolean) => void
  /** Re-renders this one lesson in the legacy editor. Shown only under `CCDEV_MODE()`. */
  switchToLegacyEditor?: () => void
}

export default tracked(function LessonMenu({
  lesson,
  course,
  planbook,
  courseDate,
  editCourseTitleAndOrder,
  pasteLesson,
  canPasteLesson,
  isFullScreen,
  copyLesson,
  copyFromAnotherClass,
  bumpLessonForward,
  pullLessonBackwards,
  dayHasTinyUnit,
  eraseLesson,
  removeClassToday,
  emailLesson,
  printOrDownload,
  shareToGoogleClassroom,
  browseLessonHistory,
  openFullScreen,
  openTemplateGallery,
  pasteTemplate,
  saveAsTemplate,
  setDefaultTemplate,
  openDefaultTemplateGallery,
  defaultTemplateId,
  onOpenChange,
  switchToLegacyEditor,
}: Props) {
  const dateString = courseDate.dateString
  const { protocol, host } = window.location
  const canEdit = lesson.meta?.permissions?.includes("can-edit-plan") ?? false
  const expandedIds = expandedSections.expandedIds
  // The rows that act on a lesson need `cardStackId`. Without it, Bump rewrites the calendar to
  // the same values and reports `lesson-null`, and the share and email links use an id that was
  // never saved. A day with only a template also has no `cardStackId`.
  const hasLesson = courseDate.hasLesson
  // Card count for "Share to Google Classroom", which uses the student version (public cards only).
  const publicCardCount = countPublicCards(lesson)

  const lessonHeader = (title: string) =>
    function Header({ back }: { back: () => void }) {
      return (
        <HeaderWithBackArrow backTo="Lesson Menu" onBack={back}>
          {title}
        </HeaderWithBackArrow>
      )
    }

  return (
    <DrilldownMenu
      ariaLabel="Lesson menu"
      trigger={<CourseHeaderMenuTrigger label="Lesson Menu" dataTest="lesson menu trigger" />}
      header={
        <MenuHeader>
          <MenuTitle>Lesson Menu</MenuTitle>
        </MenuHeader>
      }
      onOpenChange={onOpenChange}
    >
      {({ close }) => {
        const closeThen = (action: () => void) => () => {
          close()
          action()
        }
        const pullLessonsBackwardsRow = () => {
          if (dayHasTinyUnit) {
            return (
              <DrilldownRow
                typeaheadText="Pull Next Lesson Backwards"
                header={lessonHeader("Delete this day’s unit first")}
                body={({ back }) => (
                  <InfoView
                    body="To pull lessons backwards, first delete the unit that starts and ends on this date. Head over to the unit planner to delete the unit or make it longer."
                    onDone={back}
                  />
                )}
              >
                <RowContent>
                  <RowIcon icon={IconNavArrowLeft} />
                  <RowLabel>Pull Next Lesson Backwards</RowLabel>
                </RowContent>
              </DrilldownRow>
            )
          } else if (courseDate.hasLesson) {
            return (
              <DrilldownRow
                typeaheadText="Pull Next Lesson Backwards"
                header={lessonHeader("Pull future lessons back to this day?")}
                body={({ back }) => (
                  <ConfirmView
                    body="To pull future lessons back to this day, we need to delete this lesson. Trying to pull this lesson backwards instead? Go to the lesson before it and pull lessons backwards from there."
                    confirmLabel="Delete this lesson"
                    destructive
                    onCancel={back}
                    onConfirm={closeThen(pullLessonBackwards)}
                  />
                )}
              >
                <RowContent>
                  <RowIcon icon={IconNavArrowLeft} />
                  <RowLabel>Pull Next Lesson Backwards</RowLabel>
                </RowContent>
              </DrilldownRow>
            )
          } else {
            return (
              <Row
                typeaheadText="Pull Next Lesson Backwards"
                onAction={closeThen(pullLessonBackwards)}
              >
                <RowContent>
                  <RowIcon icon={IconNavArrowLeft} />
                  <RowLabel>Pull Next Lesson Backwards</RowLabel>
                </RowContent>
              </Row>
            )
          }
        }

        return (
          <MenuRoot>
            <Sections>
              {canEdit && (
                <>
                  <Section
                    typeaheadText="Copy and Paste"
                    isExpanded={expandedIds.has("copy-paste")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("copy-paste", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCommonFileDouble} />
                        <SectionTitle>Copy and Paste</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Copy Lesson"
                        onAction={closeThen(copyLesson)}
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to copy"
                      >
                        <RowContent>
                          <RowIcon icon={IconCopy} />
                          <RowLabel>Copy Lesson</RowLabel>
                        </RowContent>
                      </Row>
                      {canPasteLesson && (
                        <Row typeaheadText="Paste Lesson" onAction={closeThen(pasteLesson)}>
                          <RowContent>
                            <RowIcon icon={IconCopyPaste} />
                            <RowLabel>Paste Lesson</RowLabel>
                          </RowContent>
                        </Row>
                      )}
                      <Row
                        typeaheadText="Copy from Another Class"
                        onAction={closeThen(copyFromAnotherClass)}
                      >
                        <RowContent>
                          <RowIcon icon={IconRepo} />
                          <RowLabel>Copy from Another Class</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Move"
                    isExpanded={expandedIds.has("move")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("move", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconDoubleArrowRight} />
                        <SectionTitle>Move</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Bump Lesson Forward"
                        onAction={closeThen(bumpLessonForward)}
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to bump"
                      >
                        <RowContent>
                          <RowIcon icon={IconNavArrowRight} />
                          <RowLabel>Bump Lesson Forward</RowLabel>
                        </RowContent>
                      </Row>
                      {pullLessonsBackwardsRow()}
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Remove"
                    isExpanded={expandedIds.has("remove")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("remove", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconSubtractCircle} />
                        <SectionTitle>Remove</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Remove this class today"
                        {...removeClassTodayFlow({
                          hasLesson: courseDate.hasLesson,
                          menuTitle: "Lesson Menu",
                          courseId: course.id,
                          color: course.attributes.color,
                          dateString: courseDate.dateString,
                          onDone: removeClassToday,
                        })}
                      >
                        <RowContent>
                          <RowIcon icon={IconMinusCircle} />
                          <RowLabel>Remove this class today</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      <DrilldownRow
                        typeaheadText="Erase Lesson"
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to erase"
                        header={lessonHeader("Delete the lesson?")}
                        body={({ back }) => (
                          <ConfirmView
                            confirmLabel="Delete"
                            destructive
                            onCancel={back}
                            onConfirm={closeThen(eraseLesson)}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconBin} />
                          <RowLabel>Erase Lesson</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Print & Share"
                    isExpanded={expandedIds.has("print-share")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("print-share", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconPrintText} />
                        <SectionTitle>Print &amp; Share</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Share Link to Lesson"
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to link to"
                        header={lessonHeader("Here’s the link")}
                        body={() => (
                          <ShareLinkView
                            links={{
                              full: `${protocol}//${host}/lessons/${lesson.id}`,
                              classWebsite: studentLessonUrl(course.id, dateString),
                            }}
                            qrFileName={`${lesson.attributes.title || "Common Planner"} QR Code`}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconHyperlink} />
                          <RowLabel>Share Link to Lesson</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      <DrilldownRow
                        typeaheadText="Share to Google Classroom"
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to share"
                        header={lessonHeader(
                          publicCardCount === 0
                            ? "Choose cards to share"
                            : "Share to Google Classroom?",
                        )}
                        body={({ back }) =>
                          publicCardCount === 0 ? (
                            <InfoView
                              body={
                                <>
                                  <p className="pb-2">
                                    Before you share, at least one card needs to be visible on your
                                    class website!
                                  </p>
                                  <p>
                                    Hover over a card, click Menu, and choose “Show Card on Class
                                    Website.”
                                  </p>
                                </>
                              }
                              doneLabel="Okay"
                              onDone={close}
                            />
                          ) : (
                            <ConfirmView
                              body={
                                <>
                                  <b>Note:</b> Only the{" "}
                                  {publicCardCount === 1 ? "1 card" : `${publicCardCount} cards`}{" "}
                                  you’ve set to show on your class website will be posted to Google
                                  Classroom.
                                </>
                              }
                              confirmLabel="Share"
                              onCancel={back}
                              onConfirm={closeThen(shareToGoogleClassroom)}
                            />
                          )
                        }
                      >
                        <RowContent>
                          <RowIcon icon={IconGoogleLogo} />
                          <RowLabel>Share to Google Classroom</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      <Row
                        typeaheadText="Email Lesson"
                        onAction={closeThen(emailLesson)}
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to email"
                      >
                        <RowContent>
                          <RowIcon icon={IconEmail} />
                          <RowLabel>Email Lesson</RowLabel>
                        </RowContent>
                      </Row>
                      <Row typeaheadText="Download Lesson" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconDownload} />
                          <RowLabel>Download Lesson</RowLabel>
                        </RowContent>
                      </Row>
                      <Row typeaheadText="Print Lesson" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconPrint} />
                          <RowLabel>Print Lesson</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Templates"
                    isExpanded={expandedIds.has("templates")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("templates", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconFlashColor} className="size-5" />
                        <SectionTitle>Templates</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Replace Lesson with Template"
                        header={lessonHeader("Replace Lesson")}
                        body={() => (
                          <ReplaceWithTemplateView
                            planbook={planbook}
                            onChoose={(templateId) => {
                              close()
                              pasteTemplate(templateId)
                            }}
                            onChooseFromGallery={closeThen(openTemplateGallery)}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconFileRotate} />
                          <RowLabel>Replace Lesson with Template</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      <DrilldownRow
                        typeaheadText="Save Lesson to Template Library"
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to save"
                        header={lessonHeader("What would you like to call this template?")}
                        body={({ back }) => (
                          <TemplateNameView
                            onCancel={back}
                            onSave={(name) => {
                              close()
                              saveAsTemplate(name)
                            }}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconBookLibrary} />
                          <RowLabel>Save Lesson to Template Library</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      {/* The same list of templates as Replace above, aimed at every day the
                          class hasn't planned yet rather than at this one lesson. Only the note
                          tells them apart, which is why the view carries one. */}
                      <DrilldownRow
                        typeaheadText="Change the Default Template"
                        header={lessonHeader("Change the Default Template")}
                        body={() => (
                          <ReplaceWithTemplateView
                            planbook={planbook}
                            note={<DefaultTemplateNote />}
                            currentTemplateId={defaultTemplateId}
                            // Neither closing nor going back: the check moving to the row just
                            // pressed is the whole answer, and it is only legible here.
                            onChoose={setDefaultTemplate}
                            onChooseFromGallery={closeThen(openDefaultTemplateGallery)}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconCogLine} />
                          <RowLabel>Change the Default Template</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="History & Full Screen"
                    isExpanded={expandedIds.has("history")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("history", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconHistoryClock} />
                        <SectionTitle>History &amp; Full Screen</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Browse Lesson History"
                        onAction={closeThen(browseLessonHistory)}
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so no history to browse"
                      >
                        <RowContent>
                          <RowIcon icon={IconSyncArrowClock} />
                          <RowLabel>Browse Lesson History</RowLabel>
                        </RowContent>
                      </Row>
                      {!isFullScreen && (
                        <Row
                          typeaheadText="Full Screen Lesson"
                          onAction={closeThen(openFullScreen)}
                        >
                          <RowContent>
                            <RowIcon icon={IconFullscreen} />
                            <RowLabel>Full Screen Lesson</RowLabel>
                          </RowContent>
                        </Row>
                      )}
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Settings"
                    isExpanded={expandedIds.has("settings")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("settings", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCog1} />
                        <SectionTitle>Settings</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Edit Class Settings"
                        onAction={closeThen(() => editCourseTitleAndOrder(course.id))}
                      >
                        <RowContent>
                          <RowIcon icon={IconCogLine} />
                          <RowLabel>Edit Class Settings</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>
                </>
              )}
              {!canEdit && (
                <>
                  <Section
                    typeaheadText="Copy"
                    isExpanded={expandedIds.has("copy-readonly")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("copy-readonly", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCommonFileDouble} />
                        <SectionTitle>Copy</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Copy Lesson"
                        onAction={closeThen(copyLesson)}
                        isDisabled={!hasLesson}
                        disabledReason="Class has no lesson today, so nothing to copy"
                      >
                        <RowContent>
                          <RowIcon icon={IconCopy} />
                          <RowLabel>Copy Lesson</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>
                  <Section
                    typeaheadText="Print"
                    isExpanded={expandedIds.has("print-readonly")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("print-readonly", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconPrintText} />
                        <SectionTitle>Print</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row typeaheadText="Download Lesson" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconDownload} />
                          <RowLabel>Download Lesson</RowLabel>
                        </RowContent>
                      </Row>
                      <Row typeaheadText="Print Lesson" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconPrint} />
                          <RowLabel>Print Lesson</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>
                </>
              )}
              {devLessonEditorSwitch.isEnabled && switchToLegacyEditor && (
                <Section
                  typeaheadText="Dev"
                  isExpanded={expandedIds.has("dev")}
                  onExpandedChange={(isExpanded) => expandedSections.setExpanded("dev", isExpanded)}
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconSyncArrowClock} />
                      <SectionTitle>Dev</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    <Row
                      typeaheadText="Switch to Legacy Editor"
                      onAction={closeThen(switchToLegacyEditor)}
                    >
                      <RowContent>
                        <RowIcon icon={IconSyncArrowClock} />
                        <RowLabel>Switch to Legacy Editor</RowLabel>
                      </RowContent>
                    </Row>
                  </Rows>
                </Section>
              )}
            </Sections>
          </MenuRoot>
        )
      }}
    </DrilldownMenu>
  )
})

/**
 * How many of the lesson's cards are on the class website. Read off the stored ProseMirror JSON
 * rather than the editor: cards are the doc's top-level nodes, and the menu renders whether or
 * not this lesson's editor is mounted.
 */
function countPublicCards(lesson: UnifiedCardStack): number {
  const content = (
    lesson.attributes.proseMirror?.json as {
      content?: Array<{ attrs?: { isPublic?: unknown } }>
    }
  )?.content
  return content?.filter((node) => node?.attrs?.isPublic === true).length ?? 0
}
